// FieldUser.tsx
import React, { useEffect, useMemo, useState } from "react";
import classNames from "classnames";

import styles from "./Field.module.scss";
import Icon from "../images/Icon";

// ⚠️ Assure-toi que le chemin pointe bien vers TON composant UserSelector (celui que tu as collé au-dessus)
import UserSelector from "../../block/userManagment/UserSelector";

import { User } from "../../../dataTypes/userManagment/types";
import { getUserList } from "../../../backendServices/userManagment/api";

export interface FieldUserProps {
  placeholder?: string;
  /** valeur contrôlée : un User ou null */
  value?: User | null;
  readOnly?: boolean; // forçable, mais on garde readOnly=true par défaut
  /** onChange renvoie un User (ou null) — pas de string, pas d’event requis */
  onChange?: (value: User | null) => void;
  width?: string;
  height?: string;
  glowColor?: string;
  /** Optionnel : si fourni, on ne va pas fetch */
  users?: User[];
}

const formatUser = (u: User | null | undefined) => {
  if (!u || u.id == null) return "";
  const first = u.firstname ?? "";
  const last = u.lastname ?? "";
  return `${u.id} - ${first} - ${last}`.trim();
};

export const FieldUser: React.FC<FieldUserProps> = ({
  placeholder,
  value = null,
  readOnly = true, // champ non-éditable par défaut
  onChange,
  width = "100%",
  height = "fit-content",
  glowColor = "var(--color-Field-Focus-normal)",
  users = [],
}) => {
  const fieldStyle = { "--glow-color": glowColor } as React.CSSProperties;

  // Liste d’utilisateurs (provenant de props OU fetch backend)
  const [userSet, setUserSet] = useState<User[]>(users);
  // Utilisateur sélectionné localement
  const [selectedUser, setSelectedUser] = useState<User | null>(value);
  // Affichage (string) dérivé de selectedUser
  const displayValue = useMemo(() => formatUser(selectedUser), [selectedUser]);

  // Modal UserSelector
  const [showUserSelector, setShowUserSelector] = useState(false);

  // Si la prop users change, on synchronise
  useEffect(() => {
    if (users && users.length) {
      setUserSet(users);
    }
  }, [users]);

  // Si la prop value change (contrôlé), on synchronise l’état local
  useEffect(() => {
    setSelectedUser(value ?? null);
  }, [value]);

  // Si on n’a pas reçu de users en props, on fetch une fois
  useEffect(() => {
    if (users && users.length > 0) return;
    let cancelled = false;

    (async () => {
      try {
        const result = await getUserList();
        if (!cancelled && result?.payload) {
          setUserSet(result.payload);
        }
      } catch {
        // Optionnel: gérer l’erreur (toast, etc.)
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [users]);

  const handleOpenSelector = () => setShowUserSelector(true);
  const handleCloseSelector = () => setShowUserSelector(false);

  const handleSelectUser = (user: User) => {
    setSelectedUser(user);
    setShowUserSelector(false);
    onChange?.(user);
  };

  return (
    <div className={classNames(styles.field)} style={{ width, height, ...fieldStyle }}>
      {/* Modal UserSelector (tu peux aussi utiliser ModalDialogBox si nécessaire) */}
      {showUserSelector && (
        <UserSelector
          userSet={userSet}
          onCancel={handleCloseSelector}
          onSelect={handleSelectUser}
          componentWidth={1210}
          componentHeight={626}
        />
      )}

      <div className={classNames(styles.field_with_icon)}>
        <input
          className={classNames(styles.field_input)}
          type="text"
          placeholder={placeholder}
          value={displayValue}
         
          onClick={handleOpenSelector} // cliquer sur le champ peut aussi ouvrir le sélecteur
        />
        <Icon
          onClick={handleOpenSelector}
          name="addressBook"
          color      = "var(--color-Field-Icon-normal)"
                      hoverColor = "var(--color-Field-Icon-hover-normal)" 
        />
      </div>
    </div>
  );
};

export default FieldUser;
